import { memo, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Pin, X } from "lucide-react";
import { useAppStore } from "@/core/store/useAppStore";
import { NAV_SECTIONS, type NavItem } from "@/core/router/navConfig";
import { RouterLink } from "@/components/layout/RouterLink";

/* ------------------------------------------------------------------ */
/*  Build a lookup from path → NavItem (icon + label)                 */
/* ------------------------------------------------------------------ */

const NAV_LOOKUP: Map<string, NavItem> = new Map();
for (const section of NAV_SECTIONS) {
  for (const item of section.items) {
    NAV_LOOKUP.set(item.to, item);
  }
}

/* ------------------------------------------------------------------ */
/*  PinnedNav component                                               */
/* ------------------------------------------------------------------ */

export const PinnedNav = memo(function PinnedNav() {
  const pinnedPages = useAppStore((s) => s.pinnedPages);
  const togglePin = useAppStore((s) => s.togglePin);
  const location = useLocation();

  const pinnedItems = useMemo(
    () =>
      pinnedPages
        .map((path) => {
          const nav = NAV_LOOKUP.get(path);
          return nav ? { path, ...nav } : null;
        })
        .filter(Boolean) as (NavItem & { path: string })[],
    [pinnedPages],
  );

  if (pinnedItems.length === 0) return null;

  return (
    <nav
      className="flex items-center gap-1 border-b border-slate-700/60 bg-slate-800/60 px-4 py-1.5"
      aria-label="Pinned pages"
    >
      <Pin size={12} className="mr-1 text-slate-500 flex-shrink-0" />
      {pinnedItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <div key={item.path} className="group relative flex items-center">
            <RouterLink
              href={item.path}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? "bg-blue-600/15 text-blue-400"
                  : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
              }`}
            >
              <Icon size={13} aria-hidden="true" />
              <span>{item.label}</span>
            </RouterLink>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                togglePin(item.path);
              }}
              className="absolute -right-0.5 -top-0.5 hidden h-4 w-4 items-center justify-center rounded-full bg-slate-600 text-slate-300 hover:bg-red-600/80 hover:text-white group-hover:flex transition-colors"
              title={`Unpin ${item.label}`}
              aria-label={`Unpin ${item.label}`}
            >
              <X size={8} />
            </button>
          </div>
        );
      })}
    </nav>
  );
});
